import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { SESSION_KEY } from './auth';
import type { AssignableUser, Role, User } from '@/types/api';

export const USERS_KEY = ['users'] as const;
export const ASSIGNABLE_USERS_KEY = ['users', 'assignable'] as const;

// JsonResource collections come wrapped as { data: [...] }; hooks unwrap to the array.
export function useUsers() {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: async () => (await api.get<{ data: User[] }>('/users')).data,
  });
}

// Minimal {id, displayName} list for the Dugnadsoppgaver assignee picker — usable by
// any logged-in user (incl. registered guests), unlike useUsers() above which is
// owner+ only (ADR-0028). Enabled-gated by the caller so ad-hoc guests never fire it.
export function useAssignableUsers(enabled = true) {
  return useQuery({
    queryKey: ASSIGNABLE_USERS_KEY,
    queryFn: async () => (await api.get<{ data: AssignableUser[] }>('/users/assignable')).data,
    enabled,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; displayName: string; role: Role }) =>
      api.post<{ data: User }>('/users', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; role?: Role; displayName?: string }) =>
      api.patch<{ data: User }>(`/users/${id}`, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
}

export function useRemoveUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/users/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
      // Removing yourself ends your session — refetch it so the chrome updates.
      void queryClient.invalidateQueries({ queryKey: SESSION_KEY });
    },
  });
}
